import { useCallback, useEffect, useRef, useState } from "react";
import { AuditHeader } from "../audit/AuditApp";
import { API_BASE, KEY_STORAGE, PAGE_SIZE, STATUSES, adminFetch, authHeaders, downloadBlob } from "./adminApi";
import { AuditRow } from "./AuditRow";
import { RetentionBanner } from "./RetentionBanner";
import "../audit/Audit.css";

/* ============================================================
   LOGIN — saisie de la clé d'administration
   ============================================================ */
function AdminLogin({ onSuccess }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!key.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/admin/verify`, { headers: authHeaders(key.trim()) });
      if (res.status === 403) throw new Error("Clé d'administration invalide.");
      if (res.status === 503) throw new Error("ADMIN_KEY non configurée côté serveur (backend/.env).");
      if (!res.ok) throw new Error("Erreur serveur, merci de réessayer.");
      localStorage.setItem(KEY_STORAGE, key.trim());
      onSuccess(key.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="audit-app">
      <AuditHeader />
      <div className="audit-container admin-login-wrap">
        <form className="audit-domain-card admin-login" onSubmit={submit} data-testid="admin-login-form">
          <span className="audit-domain-tag">Console interne</span>
          <h2 className="audit-domain-title">Accès équipe Oxylog</h2>
          <p className="admin-login-lead">
            Saisissez la clé d'administration définie dans <code>backend/.env</code> (variable <code>ADMIN_KEY</code>).
            Elle sera mémorisée dans ce navigateur.
          </p>
          <div className="audit-form-field">
            <label htmlFor="admin-key">Clé d'administration</label>
            <input id="admin-key" type="password" autoComplete="current-password" value={key}
              onChange={(e) => setKey(e.target.value)} data-testid="admin-key-input" autoFocus />
          </div>
          {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
          <button type="submit" className="audit-btn audit-btn-primary" disabled={busy || !key.trim()}
            data-testid="admin-login-submit">
            {busy ? "Vérification…" : "Accéder à la console →"}
          </button>
        </form>
      </div>
    </div>
  );
}

function SortHeader({ label, field, sort, order, onSort, testId }) {
  const active = sort === field;
  return (
    <th className={`admin-th-sort${active ? " is-active" : ""}`} onClick={() => onSort(field)} data-testid={testId}>
      {label} <span aria-hidden="true">{active ? (order === "asc" ? "▲" : "▼") : "↕"}</span>
    </th>
  );
}

/* ============================================================
   DASHBOARD
   ============================================================ */
function AdminDashboard({ adminKey, onLogout }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [emailEnabled, setEmailEnabled] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/health`).then((r) => r.json())
      .then((h) => setEmailEnabled(!!h.prospect_email_enabled)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ q: query, status, sort, order, skip: String(page * PAGE_SIZE), limit: String(PAGE_SIZE) });
    try {
      const res = await adminFetch(`/audits?${params}`, adminKey);
      const data = await res.json();
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      if (e.status === 403) { onLogout(); return; }
      setError("Impossible de charger la liste des audits.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, query, status, sort, order, page, onLogout]);

  useEffect(() => { load(); }, [load]);

  function showNotice(msg) {
    setNotice(msg);
    setError("");
    clearTimeout(showNotice.t);
    showNotice.t = setTimeout(() => setNotice(""), 5000);
  }

  function onSearch(v) {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(0); setQuery(v); }, 350);
  }

  function onSort(field) {
    if (sort === field) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(field); setOrder(field === "company" ? "asc" : "desc"); }
    setPage(0);
  }

  async function exportCsv() {
    try {
      const params = new URLSearchParams({ q: query, status });
      await downloadBlob(`/audits/export.csv?${params}`, adminKey, "oxylog-audits.csv");
    } catch (e) {
      setError("Échec de l'export CSV.");
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="audit-app">
      <AuditHeader />
      <div className="audit-container admin-container" data-testid="admin-dashboard">
        <div className="admin-head">
          <div>
            <span className="audit-domain-tag">Console interne</span>
            <h2 className="admin-title">Audits reçus <span className="admin-count" data-testid="admin-total">{total}</span></h2>
          </div>
          <div className="admin-head-actions">
            <button className="audit-btn audit-btn-secondary admin-btn-sm" onClick={exportCsv} data-testid="admin-export-csv">
              Exporter CSV
            </button>
            <button className="audit-btn audit-btn-ghost admin-btn-sm" onClick={onLogout} data-testid="admin-logout">
              Déconnexion
            </button>
          </div>
        </div>

        <RetentionBanner adminKey={adminKey} onError={setError}
          onPurged={(n) => { showNotice(`Purge effectuée : ${n} audit${n > 1 ? "s" : ""} supprimé${n > 1 ? "s" : ""}.`); load(); }} />

        <div className="admin-toolbar">
          <input type="search" className="admin-search" placeholder="Rechercher une société, un nom, un email…"
            value={q} onChange={(e) => onSearch(e.target.value)} data-testid="admin-search-input" />
          <select className="admin-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            data-testid="admin-status-filter">
            <option value="">Tous les statuts</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <span className="admin-muted" data-testid="admin-result-count">
            {loading ? "Chargement…" : `${total} résultat${total > 1 ? "s" : ""}`}
          </span>
        </div>

        {error && <p className="admin-error" data-testid="admin-error">{error}</p>}
        {notice && <p className="admin-notice" data-testid="admin-notice">{notice}</p>}

        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="admin-table">
            <thead>
              <tr>
                <SortHeader label="Date" field="created_at" sort={sort} order={order} onSort={onSort} testId="admin-sort-date" />
                <SortHeader label="Société / Contact" field="company" sort={sort} order={order} onSort={onSort} testId="admin-sort-company" />
                <th>Coordonnées</th>
                <SortHeader label="Score" field="overall_score" sort={sort} order={order} onSort={onSort} testId="admin-sort-score" />
                <th>Suivi</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <AuditRow key={a.id} audit={a} adminKey={adminKey} emailEnabled={emailEnabled}
                  onUpdated={(u) => setItems((l) => l.map((x) => (x.id === u.id ? { ...x, ...u } : x)))}
                  onDeleted={(id) => { setItems((l) => l.filter((x) => x.id !== id)); setTotal((t) => t - 1); }}
                  onError={setError} onNotice={showNotice} />
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="admin-empty" data-testid="admin-empty">Aucun audit trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="admin-pagination" data-testid="admin-pagination">
            <button className="audit-btn audit-btn-ghost admin-btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              data-testid="admin-prev-page">← Précédent</button>
            <span className="admin-muted">Page {page + 1} / {pages}</span>
            <button className="audit-btn audit-btn-ghost admin-btn-sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}
              data-testid="admin-next-page">Suivant →</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ENTRÉE — gère la clé mémorisée
   ============================================================ */
export function AdminConsole() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");

  const logout = useCallback(() => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
  }, []);

  if (!adminKey) return <AdminLogin onSuccess={setAdminKey} />;
  return <AdminDashboard adminKey={adminKey} onLogout={logout} />;
}
